import { ProjectDto, SettingsSchemaDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { RJSFSchema } from '@rjsf/utils';

export class RegistrationPageFormSchema {
  static FromSettingsSchema(
    projectOptions: ProjectDto[],
    mode: 'create' | 'edit',
    settingsSchema?: SettingsSchemaDto
  ): RJSFSchema {
    const schema: RJSFSchema = {
      required: ['urlSlug'],
      properties: {
        allowedProjectIds: {
          type: 'array',
          title: 'Selectable Child Projects',
          items: {
            type: 'string',
            ...(projectOptions.length > 0 && {
              anyOf: projectOptions.map((p) => ({
                type: 'string',
                title: p.name,
                enum: [p.id]
              }))
            })
          },
          readOnly: projectOptions.length === 0,
          description:
            projectOptions.length > 0
              ? `By default, occupants can register for the project associated with this registration page. Alternatively, select child projects to allow occupants to choose from during registration.`
              : `No child projects are available to select. Occupants will only be able to register for the project associated with this registration page.`
        },
        ...(settingsSchema && {
          urlSlug: {
            type: 'string',
            title: 'Unique URL extension (permanent)',
            pattern: RegistrationPageFormSchema.GetRegexPattern('urlSlug', settingsSchema),
            readOnly: mode === 'edit',
            description: `A unique URL extension that will be used to access the registration page. This value cannot be changed once set.`
          },
          unitLabel: {
            type: 'string',
            title: 'Unit Label',
            pattern: RegistrationPageFormSchema.GetRegexPattern('unitLabel', settingsSchema),
            description: `Label used for the Unit input field (e.g., "Unit", "Apartment", "Suite").`
          },
          headerTextColor: {
            type: 'string',
            title: 'Header Text Color',
            pattern: RegistrationPageFormSchema.GetRegexPattern('headerTextColor', settingsSchema),
            description: `Hex code for the header text color of the registration page in the format #RRGGBB.`
          },
          bgColor: {
            type: 'string',
            title: 'Background Color',
            pattern: RegistrationPageFormSchema.GetRegexPattern('bgColor', settingsSchema),
            description: `Hex code for the background color of the registration page in the format #RRGGBB.`
          },
          logoUrl: {
            type: 'string'
          },
          addressOverride: {
            type: 'string',
            title: 'Address Override',
            pattern: RegistrationPageFormSchema.GetRegexPattern('addressOverride', settingsSchema),
            description: `Overrides the Project's address displayed as part of the occupant's address information.`
          },
          tncOverride: {
            type: 'string',
            title: 'Terms & Conditions Override',
            pattern: RegistrationPageFormSchema.GetRegexPattern('tncOverride', settingsSchema),
            description: `Overrides the default Terms & Conditions text presented to occupants during registration.`
          },
          thankYouOverride: {
            type: 'string',
            title: 'Thank You Override',
            pattern: RegistrationPageFormSchema.GetRegexPattern('thankYouOverride', settingsSchema),
            description: `Overrides the default Thank You message shown to occupants after completing registration.`
          }
        })
      }
    };
    return schema;
  }

  static GetRegexPattern(key: string, settingsSchema: SettingsSchemaDto): string | undefined {
    const keySchema = settingsSchema.schema[key];
    if (typeof keySchema !== 'object' || keySchema === null) {
      return undefined;
    }
    return (keySchema as Record<string, any>).validation?.pattern;
  }
}
